import { useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts/core";
import { useSelector } from "react-redux";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { DataScienceContext } from "../context/DataScienceContext";
import { useTranslation } from "react-i18next";
import {
  getMoistureContentDisplayMode,
  getMoistureContentCapEnabled,
  scaleMoistureThresholdForDisplay,
} from "@helper/moistureContentDisplay";
import "../DataScience.css";
import { getVariableById, getVariableDisplayLabel } from "../utils/variableMetadata";

export const UNIT_BY_VARIABLE = {
  resistance: "kΩ",
  temperature: "°C",
  moisture_content: "%",
  soil_water_tension: "kPa",
  soil_water_tension_vg: "kPa",
  diameter: "µm",
  diameter_minus_start: "µm",
  tree_water_deficit: "µm",
  soil_moisture: "%",
  soil_moisture_vg: "%",
};

const style = {
  height: "100%",
  width: "100%",
};

const HEALTH_STATE_LABEL_BY_VALUE = {
  0: "healthy",
  1: "okay",
  2: "critical",
  3: "sensor_disconnect",
  4: "maintenance_needed",
};

function binHistogram(data: { value: [string, number] }[], binSize = 10) {
  const bins: Record<number, number> = {};
  for (const d of data) {
    const val = d.value[1];
    const bin = Math.floor(val / binSize) * binSize;
    bins[bin] = (bins[bin] || 0) + 1;
  }
  return Object.entries(bins)
    .sort(([a], [b]) => +a - +b)
    .map(([bin, count]) => ({
      name: `${bin}–${+bin + binSize}`,
      value: count,
    }));
}

const getAxisLabel = (dragItem, t, moistureMode?, variableId?: number | null) => {
  if (!dragItem) return "";

  const def = variableId != null ? getVariableById(variableId) : null;
  const displayName = def ? getVariableDisplayLabel(def, t) : t(dragItem);
  const unit = getMoistureAwareUnit(dragItem, moistureMode);
  return unit ? `${displayName} in ${unit}` : displayName;
};

export const getMoistureAwareUnit = (variable, moistureMode?) => {
  if (variable === "moisture_content" && moistureMode === "fraction") return "";
  return UNIT_BY_VARIABLE[variable] || "";
};

/**
 * Collect all finite numeric y-values from series belonging to a specific yAxisIndex.
 * Skips silent threshold series and hidden series (lineStyle.opacity === 0).
 */
const getNumericSeriesValues = (seriesList, yAxisIndex) => {
  const values = [];
  (seriesList || []).forEach((s) => {
    if (!s || s.yAxisIndex !== yAxisIndex) return;
    if (s.silent) return;
    if (s.lineStyle?.opacity === 0) return;
    (s.data || []).forEach((point) => {
      const v = point?.value?.[1] ?? (typeof point?.value === "number" ? point.value : null);
      if (v == null && typeof point === "number" && Number.isFinite(point)) {
        values.push(point);
        return;
      }
      if (Number.isFinite(v)) values.push(v);
    });
  });
  return values;
};

/**
 * Round a bound value to produce clean axis labels.
 * Uses 2 significant digits for the padding portion so labels stay readable.
 */
const roundBound = (value, direction) => {
  if (!Number.isFinite(value)) return value;
  const abs = Math.abs(value);
  if (abs === 0) return 0;

  let precision;
  if (abs >= 1000) precision = 1;
  else if (abs >= 100) precision = 1;
  else if (abs >= 10) precision = 0.5;
  else if (abs >= 1) precision = 0.1;
  else precision = 0.01;

  const rounded =
    direction === "down"
      ? Math.floor(value / precision) * precision
      : Math.ceil(value / precision) * precision;

  const decimals = precision >= 1 ? 0 : String(precision).split(".")[1]?.length || 0;
  return Number(rounded.toFixed(decimals));
};

/**
 * Compute dynamic min/max for a y-axis based on actual plotted data.
 * - data min/max with ~10% padding, allowing negatives
 * - Returns { min: null, max: null } when no data exists (let ECharts auto-scale)
 */
const getDynamicAxisBounds = (seriesList, yAxisIndex) => {
  const values = getNumericSeriesValues(seriesList, yAxisIndex);
  if (values.length === 0) return { min: null, max: null };

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  const AXIS_PADDING_RATIO = 0.05;

  if (dataMin === dataMax) {
    const offset = Math.abs(dataMin) * AXIS_PADDING_RATIO || 1;
    return {
      min: roundBound(dataMin - offset, "down"),
      max: roundBound(dataMax + offset, "up"),
    };
  }

  const range = dataMax - dataMin;
  const padding = range * AXIS_PADDING_RATIO;
  return {
    min: roundBound(dataMin - padding, "down"),
    max: roundBound(dataMax + padding, "up"),
  };
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const getSeriesBaseColor = (seriesEntry) => {
  if (seriesEntry?.lineStyle?.color) return seriesEntry.lineStyle.color;
  if (seriesEntry?.itemStyle?.color) return seriesEntry.itemStyle.color;
  if (seriesEntry?.color) return seriesEntry.color;

  const firstPointWithColor = (seriesEntry?.data || []).find(
    (point) => point?.color || point?.itemStyle?.color
  );

  return firstPointWithColor?.color || firstPointWithColor?.itemStyle?.color || null;
};

const decorateSeriesForHover = (seriesEntry) => {
  if (!seriesEntry) return seriesEntry;
  if (seriesEntry?.silent) {
    return {
      ...seriesEntry,
      emphasis: { disabled: true },
      blur: { lineStyle: { opacity: seriesEntry?.lineStyle?.opacity ?? 1 } },
    };
  }

  const baseColor = getSeriesBaseColor(seriesEntry);

  if (seriesEntry.type === "line") {
    return {
      ...seriesEntry,
      color: baseColor || seriesEntry.color,
      symbol: "circle",
      showSymbol: false,
      itemStyle: {
        ...(seriesEntry.itemStyle || {}),
        ...(baseColor ? { color: baseColor } : {}),
      },
      emphasis: {
        ...(seriesEntry.emphasis || {}),
        focus: "self",
        scale: false,
        itemStyle: {
          ...(seriesEntry.emphasis?.itemStyle || {}),
          ...(baseColor ? { color: baseColor } : {}),
          borderColor: "#fff",
          borderWidth: 2,
        },
        lineStyle: {
          ...(seriesEntry.emphasis?.lineStyle || {}),
          ...(baseColor ? { color: baseColor } : {}),
          opacity: seriesEntry?.lineStyle?.opacity ?? 1,
        },
      },
    };
  }

  if (seriesEntry.type === "bar") {
    return {
      ...seriesEntry,
      emphasis: {
        ...(seriesEntry.emphasis || {}),
        focus: "self",
        itemStyle: {
          ...(seriesEntry.emphasis?.itemStyle || {}),
          ...(baseColor ? { color: baseColor } : {}),
        },
      },
    };
  }

  return seriesEntry;
};

const findClosestPointIndex = (chart, seriesIndex, data, cursor) => {
  if (!chart || !Array.isArray(data) || data.length === 0 || !Array.isArray(cursor)) return -1;

  let closestIndex = -1;
  let closestDistance = Number.POSITIVE_INFINITY;

  data.forEach((point, dataIndex) => {
    if (!Array.isArray(point?.value)) return;

    const pixel = chart.convertToPixel({ seriesIndex }, point.value);
    if (!Array.isArray(pixel) || pixel.length < 2) return;

    const dx = pixel[0] - cursor[0];
    const dy = pixel[1] - cursor[1];
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = dataIndex;
    }
  });

  return closestIndex;
};

const sanitizeExcelSheetName = (name: string, fallback = "Sheet") => {
  const cleaned = String(name || fallback)
    .replace(/[:\\/?*[\]]/g, "_")
    .trim();
  return (cleaned || fallback).slice(0, 31);
};

/**
 * Ensures all sheet names in the array are unique by appending numbers if needed.
 */
const ensureUniqueSheetNames = (sheets: { sheetName: string }[]) => {
  const usedNames = new Set<string>();
  sheets.forEach((sheet) => {
    let name = sheet.sheetName;
    let counter = 1;
    while (usedNames.has(name)) {
      const suffix = ` (${counter})`;
      // Ensure we don't exceed 31 chars after adding suffix
      const maxBaseLength = 31 - suffix.length;
      name = sheet.sheetName.slice(0, maxBaseLength) + suffix;
      counter++;
    }
    usedNames.add(name);
    sheet.sheetName = name;
  });
};

interface SeriesExportData {
  sheetName: string;
  treeName: string;
  sensorName: string;
  variable: string;
  axis: "left" | "right";
  rows: Array<{
    time: string;
    value: number | null;
    formula_type?: string;
    calibration_species?: string;
  }>;
}

/**
 * Prepares chart data for CSV/Excel/JSON export.
 * Each sensor/installation becomes a separate sheet with proper variable labeling.
 */
const prepareChartDataForExport = (
  lYaxis,
  rYaxis,
  startDate,
  endDate,
  dragItemLeft,
  dragItemRight
) => {
  const sheets: SeriesExportData[] = [];

  // Process left Y-axis data (one sheet per sensor)
  (lYaxis || []).forEach((series) => {
    if (!series?.name || !Array.isArray(series.data) || series.data.length === 0) return;

    const installation = series.installation || series.data[0]?.installation;
    const treeName = installation?.tree?.name || "";
    const sensorName = installation?.sensor?.name || "";
    const variable = series.label || dragItemLeft || "value";

    // Include variable in sheet name to avoid duplicates when same sensor is used for both axes
    const sheetNameWithVar = `${series.name} (${variable})`;

    const rows = series.data.map((point) => {
      const [timestamp, value] = point?.value || [];
      return {
        time: timestamp,
        [variable]: value ?? null,
        ...(point?.formulaType ? { formula_type: point.formulaType } : {}),
        ...(point?.calibrationSpecies ? { calibration_species: point.calibrationSpecies } : {}),
      };
    });

    sheets.push({
      sheetName: sanitizeExcelSheetName(sheetNameWithVar),
      treeName,
      sensorName,
      variable,
      axis: "left",
      rows,
    });
  });

  // Process right Y-axis data (one sheet per sensor)
  (rYaxis || []).forEach((series) => {
    if (!series?.name || !Array.isArray(series.data) || series.data.length === 0) return;

    const installation = series.installation || series.data[0]?.installation;
    const treeName = installation?.tree?.name || "";
    const sensorName = installation?.sensor?.name || "";
    const variable = series.label || dragItemRight || "value";

    // Include variable in sheet name to avoid duplicates when same sensor is used for both axes
    const sheetNameWithVar = `${series.name} (${variable})`;

    const rows = series.data.map((point) => {
      const [timestamp, value] = point?.value || [];
      return {
        time: timestamp,
        [variable]: value ?? null,
        ...(point?.formulaType ? { formula_type: point.formulaType } : {}),
        ...(point?.calibrationSpecies ? { calibration_species: point.calibrationSpecies } : {}),
      };
    });

    sheets.push({
      sheetName: sanitizeExcelSheetName(sheetNameWithVar),
      treeName,
      sensorName,
      variable,
      axis: "right",
      rows,
    });
  });

  return {
    sheets,
    meta: {
      startDate,
      endDate,
      exportedAt: new Date().toISOString(),
      totalSheets: sheets.length,
      variables: {
        left: dragItemLeft || null,
        right: dragItemRight || null,
      },
    },
  };
};

function GraphArea({ checkLoading, rainData, irrigationData, anyFetchSuccessfull }) {
  const {
    lYaxis,
    rYaxis,
    lYaxisMean,
    rYaxisMean,
    healthData,
    dragItemLeft,
    dragItemRight,
    dragItemLeftId,
    dragItemRightId,
    activeRain,
    activeIrrigationData,
    chartType,
    filterState,
    activeInstallations,
    showMoistureThresholds,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setZoomTriggeredDateChange,
    showHideLeftData,
    showHideRightData,
  } = useContext(DataScienceContext);
  const chartInstance = useRef(null);
  const { t } = useTranslation();
  const authUser = useSelector((state: any) => state.auth.user);
  const moistureContentDisplayMode = getMoistureContentDisplayMode(authUser);
  const moistureContentCapEnabled = getMoistureContentCapEnabled(authUser);

  const [isCompactLandscape, setIsCompactLandscape] = useState(() => {
    return window.innerWidth > window.innerHeight && window.innerHeight <= 520;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia("(max-height: 520px) and (orientation: landscape)");
    const handler = (e) => setIsCompactLandscape(e.matches);
    setIsCompactLandscape(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    if (typeof mql.addListener === "function") {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
    return undefined;
  }, []);

  const thresholdTree = useMemo(() => {
    const filterTrees = Array.isArray(filterState?.trees) ? filterState.trees : [];
    const selectedTreeIds = Array.isArray(filterState?.treesId) ? filterState.treesId : [];
    if (selectedTreeIds.length === 1) {
      const selectedTreeId = selectedTreeIds[0];
      const selectedTreeFromFilter = filterTrees.find((tree) => tree?.id === selectedTreeId);
      if (selectedTreeFromFilter) return selectedTreeFromFilter;

      const selectedTreeFromInstallations = (activeInstallations || [])
        .map((inst) => inst?.tree)
        .find((tree) => tree?.id === selectedTreeId);
      if (selectedTreeFromInstallations) return selectedTreeFromInstallations;
    }
    if (filterTrees.length === 1) return filterTrees[0];

    const installationTrees = (activeInstallations || []).map((inst) => inst?.tree).filter(Boolean);
    const uniqueById = [];
    const seen = new Set();
    installationTrees.forEach((tree) => {
      const id = tree?.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      uniqueById.push(tree);
    });
    if (uniqueById.length === 1) return uniqueById[0];
    return null;
  }, [activeInstallations, filterState?.trees, filterState?.treesId]);

  const moistureThresholdsVisible = showMoistureThresholds && Boolean(thresholdTree);

  const irrigationAxisMax = useMemo(() => {
    const irrigationSeries = (irrigationData || []).find((s) => s?.name === "irrigation");
    const values = (irrigationSeries?.data || [])
      .map((point) => point?.value?.[1])
      .filter((v) => Number.isFinite(v));
    if (!values.length) return 10;
    const maxValue = Math.max(...values);
    return Math.max(10, Math.ceil(maxValue * 1.2));
  }, [irrigationData]);

  const rainAxisMax = useMemo(() => {
    const values = (rainData || [])
      .flatMap((series) => series?.data || [])
      .map((point) => point?.value?.[1])
      .filter((value) => Number.isFinite(value));
    if (!values.length) return 1;
    return Math.max(1, Math.ceil(Math.max(...values) * 1.1));
  }, [rainData]);

  const hasRainSeries = useMemo(
    () => (rainData || []).some((s) => Array.isArray(s?.data) && s.data.length > 0),
    [rainData]
  );
  const hasIrrigationSeries = useMemo(
    () => (irrigationData || []).some((s) => Array.isArray(s?.data) && s.data.length > 0),
    [irrigationData]
  );

  let binned = null;

  chartType === "histogram" && lYaxis?.length === 1 && !dragItemRight
    ? (binned = binHistogram(lYaxis[0].data, 10))
    : [];
  const histogramSeries =
    chartType === "histogram" && lYaxis?.length === 1 && !dragItemRight
      ? (() => {
          return [
            {
              type: "bar",
              name: t(lYaxis[0].name),
              data: binned.map((d) => d.value),
              itemStyle: {
                color: lYaxis[0].color,
              },
            },
          ];
        })()
      : [];

  const xAxis =
    chartType === "histogram"
      ? [
          {
            type: "category",
            boundaryGap: true,
            data: lYaxis?.length === 1 && !dragItemRight ? binned.map((d) => d.name) : [],
            axisLabel: {
              rotate: 45,
            },
          },
        ]
      : [
          {
            type: "time",
            axisPointer: { show: true, type: "line" },
            axisLine: { show: false },
            splitLine: {
              show: true,
              interval: "0",
              lineStyle: { color: ["#F5F5F5"] },
            },
            axisLabel: {
              hideOverlap: true,
              formatter: function (value) {
                const fmt = isCompactLandscape ? "DD.MM. HH:mm" : "DD.MM.YYYY HH:mm";
                return moment.utc(value).format(fmt);
              },
            },
            offset: isCompactLandscape ? 4 : 10,
          },
        ];

  function getMarkLines(tree, variable) {
    if (!tree) return [];

    if (variable === "moisture_content") {
      const yellowThreshold = scaleMoistureThresholdForDisplay(
        tree?.yellow_threshold ?? 50,
        moistureContentDisplayMode,
        moistureContentCapEnabled
      );
      const greenThreshold = scaleMoistureThresholdForDisplay(
        tree?.green_threshold ?? 70,
        moistureContentDisplayMode,
        moistureContentCapEnabled
      );
      const blueThreshold = scaleMoistureThresholdForDisplay(
        tree?.blue_threshold ?? 100,
        moistureContentDisplayMode,
        moistureContentCapEnabled
      );
      return [
        {
          yAxis: blueThreshold,
          lineStyle: { color: "green", type: "dashed" },
          label: { formatter: t("healthy"), position: "insideEndTop" },
        },
        {
          yAxis: greenThreshold,
          lineStyle: { color: "yellow", type: "dashed" },
          label: { formatter: t("okay"), position: "insideEndTop" },
        },
        {
          yAxis: yellowThreshold,
          lineStyle: { color: "red", type: "dashed" },
          label: { formatter: t("critical"), position: "insideEndTop" },
        },
      ];
    }

    if (variable === "soil_moisture") {
      const soil = tree?.soil;
      const yellowThreshold = soil?.pwp ?? 10;
      const greenThreshold =
        soil && Number(soil.max_nf) > 0
          ? soil.pwp + ((soil.max_nf / 2) * (soil.max_nf - soil.pwp)) / soil.max_nf
          : 25;
      const blueThreshold = soil?.max_nf ?? 45;
      return [
        {
          yAxis: blueThreshold,
          lineStyle: { color: "green", type: "dashed" },
          label: { formatter: t("healthy"), position: "insideEndTop" },
        },
        {
          yAxis: greenThreshold,
          lineStyle: { color: "yellow", type: "dashed" },
          label: { formatter: t("okay"), position: "insideEndTop" },
        },
        {
          yAxis: yellowThreshold,
          lineStyle: { color: "red", type: "dashed" },
          label: { formatter: t("critical"), position: "insideEndTop" },
        },
      ];
    }

    if (variable === "soil_water_tension") {
      const soil = tree?.soil;
      const yellowThreshold =
        soil?.yellow_threshold_swt != null ? -Math.pow(10, soil.yellow_threshold_swt) / 10 : -120;
      const greenThreshold =
        soil?.green_threshold_swt != null ? -Math.pow(10, soil.green_threshold_swt) / 10 : -60;
      const blueThreshold =
        soil?.blue_threshold_swt != null ? -Math.pow(10, soil.blue_threshold_swt) / 10 : -6;
      return [
        {
          yAxis: blueThreshold,
          lineStyle: { color: "green", type: "dashed" },
          label: { formatter: t("healthy"), position: "insideEndTop" },
        },
        {
          yAxis: greenThreshold,
          lineStyle: { color: "yellow", type: "dashed" },
          label: { formatter: t("okay"), position: "insideEndTop" },
        },
        {
          yAxis: yellowThreshold,
          lineStyle: { color: "red", type: "dashed" },
          label: { formatter: t("critical"), position: "insideEndTop" },
        },
      ];
    }

    return [];
  }

  const buildThresholdSeries = (markLines, yAxisIndex) => {
    if (!Array.isArray(markLines) || markLines.length === 0) return [];
    if (!startDate || !endDate) return [];

    const start = `${startDate}T00:00:00Z`;
    const end = `${endDate}T23:59:59Z`;

    return markLines
      .filter((line) => Number.isFinite(line?.yAxis))
      .map((line) => ({
        name: String(line?.label?.formatter || "threshold"),
        type: "line",
        yAxisIndex,
        tooltip: { show: false },
        smooth: false,
        silent: true,
        symbol: "none",
        lineStyle: {
          width: 1,
          type: "dashed",
          color: line?.lineStyle?.color || "#999",
        },
        data: [{ value: [start, line.yAxis] }, { value: [end, line.yAxis] }],
      }));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasPerPointThresholds = (seriesArray: any[]): boolean =>
    (seriesArray || []).some((s) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (s.data || []).some(
        (p: any) =>
          // eslint-disable-next-line prettier/prettier
          p?.blueThreshold != null || p?.greenThreshold != null || p?.yellowThreshold != null
      )
    );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildPerPointThresholdSeries = (seriesArray: any[], yAxisIndex: number, side: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allPoints: any[] = [];
    (seriesArray || []).forEach((s) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (s.data || []).forEach((p: any) => {
        if (p?.blueThreshold != null || p?.greenThreshold != null || p?.yellowThreshold != null)
          allPoints.push(p);
      })
    );
    if (allPoints.length === 0) return [];

    const configs = [
      { key: "blueThreshold", color: "green", label: t("healthy") },
      { key: "greenThreshold", color: "yellow", label: t("okay") },
      { key: "yellowThreshold", color: "red", label: t("critical") },
    ];

    return configs
      .map(({ key, color, label }) => {
        const data = allPoints
          .filter((p) => p[key] != null && Number.isFinite(p[key]))
          .map((p) => ({ value: [p.value[0], p[key]] }));
        if (data.length === 0) return null;
        return {
          name: `${side}-${label}-threshold`,
          type: "line",
          yAxisIndex,
          tooltip: { show: false },
          smooth: false,
          silent: true,
          symbol: "none",
          lineStyle: { width: 1, type: "dashed", color },
          data,
        };
      })
      .filter(Boolean);
  };

  const getOption = () => {
    const hasLeftVarData = getNumericSeriesValues(computedSeries, 0).length > 0;
    const hasRightVarData = getNumericSeriesValues(computedSeries, 1).length > 0;

    const showLeftVarAxis = Boolean(dragItemLeft && !showHideLeftData && hasLeftVarData);
    const showRightVarAxis = Boolean(dragItemRight && !showHideRightData && hasRightVarData);
    const showRainAxis = Boolean(activeRain && hasRainSeries);
    const showIrrigationAxis = Boolean(activeIrrigationData && hasIrrigationSeries);

    const getSeriesUnit = (item) => {
      const variableType = item?.data?.type;
      if (variableType && UNIT_BY_VARIABLE[variableType]) {
        return getMoistureAwareUnit(variableType, moistureContentDisplayMode);
      }
      if (item?.seriesName === "rain") return "mm";
      if (item?.seriesName === "irrigation" || item?.data?.type === "irrigation") return "L";
      return "";
    };

    const getSeriesLabel = (item) => {
      if (item?.seriesName === "rain") return t("rainfall_mm");
      if (item?.seriesName === "irrigation" || item?.data?.type === "irrigation") {
        return t("care_chat.water_amount_liters");
      }
      if (item?.seriesName === "health") return t("health_states_title");
      return item?.seriesName || t(item?.data?.type || "");
    };

    const getSeriesMeta = (item) => {
      const variableType = item?.data?.type;
      if (item?.seriesName === "health") {
        const stateKey = HEALTH_STATE_LABEL_BY_VALUE[item?.data?.state];
        return stateKey ? t(stateKey) : "";
      }
      if (variableType && variableType !== "irrigation") {
        return t(variableType);
      }
      if (item?.data?.title) return item.data.title;
      return "";
    };

    const getTooltipSource = (item) => {
      const installation = item?.data?.installation;
      const treeName = installation?.tree?.name || "";
      const sensorName = installation?.sensor?.name || "";

      if (treeName || sensorName) {
        return { treeName, sensorName };
      }

      if (item?.seriesName === "rain") {
        return { treeName: "", sensorName: t("rainfall_mm") };
      }

      if (item?.seriesName === "irrigation" || item?.data?.type === "irrigation") {
        return { treeName: "", sensorName: t("care_chat.water_amount_liters") };
      }

      if (item?.seriesName === "health") {
        return { treeName: "", sensorName: t("health_states_title") };
      }

      return { treeName: "", sensorName: item?.seriesName || "" };
    };

    const formatSeriesValue = (item) => {
      if (item?.seriesName === "health") {
        const stateKey = HEALTH_STATE_LABEL_BY_VALUE[item?.data?.state];
        return stateKey ? t(stateKey) : "—";
      }

      const rawValue = item?.value?.[1];
      if (rawValue == null || rawValue === "") return "—";

      const numericValue = Number(rawValue);
      const formattedValue = Number.isFinite(numericValue)
        ? new Intl.NumberFormat(undefined, {
            maximumFractionDigits: Math.abs(numericValue) >= 100 ? 0 : 2,
          }).format(numericValue)
        : String(rawValue);

      const unit = getSeriesUnit(item);
      return unit ? `${formattedValue} ${unit}` : formattedValue;
    };

    const leftAxisBounds = getDynamicAxisBounds(computedSeries, 0);
    const rightAxisBounds = getDynamicAxisBounds(computedSeries, 1);

    let gridRight = 30;
    if (showRightVarAxis) gridRight += 45;
    if (showRainAxis) gridRight += 40;
    if (showIrrigationAxis) gridRight += 45;

    return {
      legend: {
        show: false,
      },
      toolbox: {
        show: true,
        top: 10,
        right: 10,
        itemSize: 18,
        itemGap: 6,
        zlevel: 10,
        orient: "vertical",
        feature: {
          dataZoom: {
            title: {
              zoom: t("zoom_in"),
              back: t("reset_zoom"),
            },
          },
          restore: { title: t("restore_view") },
          mySaveAsPNG: {
            show: true,
            title: "PNG Export",
            icon: "path://M19,3H5C3.9,3,3,3.9,3,5v14c0,1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2V5C21,3.9,20.1,3,19,3z M19,19H5V5h14V19z M13.96,12.29l-2.75,3.54l-1.96-2.36L6.5,17h11L13.96,12.29z",
            onclick: function () {
              const chart = chartInstance.current;
              if (!chart) return;

              const currentOption = chart.getOption();
              const currentSeries = Array.isArray(currentOption.series) ? currentOption.series : [];

              const exportLegendData = currentSeries
                .filter((s) => s && !s.silent && s.name && s.type !== "custom")
                .map((s) => {
                  const color = s.lineStyle?.color || s.itemStyle?.color || s.color || "#000";
                  return {
                    name: s.name,
                    icon: s.type === "line" ? "roundRect" : "rect",
                    itemStyle: { color: color },
                  };
                });

              const exportContainer = document.createElement("div");
              exportContainer.style.position = "absolute";
              exportContainer.style.left = "-9999px";
              exportContainer.style.top = "0";
              exportContainer.style.width = "1400px";
              exportContainer.style.height = "900px";
              document.body.appendChild(exportContainer);

              const exportChart = echarts.init(exportContainer, null, { renderer: "canvas" });

              const currentGrid = Array.isArray(currentOption.grid)
                ? currentOption.grid[0]
                : currentOption.grid;
              const currentXAxis = Array.isArray(currentOption.xAxis)
                ? currentOption.xAxis[0]
                : currentOption.xAxis;
              const currentYAxis = Array.isArray(currentOption.yAxis) ? currentOption.yAxis : [];

              const itemsPerRow = 4;
              const legendRows = Math.ceil(exportLegendData.length / itemsPerRow);
              const legendSpacing = legendRows * 25 + 40;

              const variableLegendGraphics = [];
              let yOffset = 15;
              if (dragItemLeft) {
                const label = getAxisLabel(
                  dragItemLeft,
                  t,
                  moistureContentDisplayMode,
                  dragItemLeftId
                );
                variableLegendGraphics.push({
                  type: "group",
                  right: 15,
                  top: yOffset,
                  children: [
                    {
                      type: "line",
                      shape: { x1: 0, y1: 5, x2: 20, y2: 5 },
                      style: { stroke: "#102532", lineWidth: 2 },
                    },
                    {
                      type: "text",
                      left: 25,
                      style: {
                        text: label,
                        fill: "#102532",
                        font: "12px sans-serif",
                      },
                    },
                  ],
                });
                yOffset += 20;
              }
              if (dragItemRight) {
                const label = getAxisLabel(
                  dragItemRight,
                  t,
                  moistureContentDisplayMode,
                  dragItemRightId
                );
                variableLegendGraphics.push({
                  type: "group",
                  right: 15,
                  top: yOffset,
                  children: [
                    {
                      type: "line",
                      shape: { x1: 0, y1: 5, x2: 20, y2: 5 },
                      style: { stroke: "#102532", lineWidth: 2, lineDash: [4, 2] },
                    },
                    {
                      type: "text",
                      left: 25,
                      style: {
                        text: label,
                        fill: "#102532",
                        font: "12px sans-serif",
                      },
                    },
                  ],
                });
              }

              const exportOption = {
                animation: false,
                xAxis: currentXAxis,
                yAxis: currentYAxis,
                series: currentSeries,
                tooltip: currentOption.tooltip,
                dataZoom: currentOption.dataZoom,
                graphic: variableLegendGraphics,
                legend: {
                  show: true,
                  type: "plain",
                  orient: "horizontal",
                  bottom: 10,
                  left: "center",
                  padding: [5, 10],
                  itemWidth: 14,
                  itemHeight: 10,
                  itemGap: 15,
                  textStyle: {
                    fontSize: 12,
                    color: "#102532",
                  },
                  data: exportLegendData,
                },
                grid: {
                  ...currentGrid,
                  bottom: legendSpacing + 60,
                  left: 60,
                  right: 60,
                  top: 50,
                },
              };

              exportChart.setOption(exportOption);

              setTimeout(() => {
                try {
                  const imgUrl = exportChart.getDataURL({
                    type: "png",
                    pixelRatio: 2,
                    backgroundColor: "#fff",
                  });

                  const link = document.createElement("a");
                  link.href = imgUrl;
                  link.download = `data-science-${new Date().toISOString().split("T")[0]}.png`;
                  link.click();
                } catch (e) {
                  console.error("PNG export failed:", e);
                }

                exportChart.dispose();
                document.body.removeChild(exportContainer);
              }, 500);
            },
          },
          mySaveAsSVG: {
            show: true,
            title: "SVG Export",
            icon: "path://M12,2 L2,12 L12,22 L22,12 Z M12,5 L5,12 L12,19 L19,12 Z M10,10 h4 v4 h-4 Z",
            onclick: function () {
              const chart = chartInstance.current;
              if (!chart) return;

              const currentOption = chart.getOption();
              const currentSeries = Array.isArray(currentOption.series) ? currentOption.series : [];

              const exportLegendData = currentSeries
                .filter((s) => s && !s.silent && s.name && s.type !== "custom")
                .map((s) => {
                  const color = s.lineStyle?.color || s.itemStyle?.color || s.color || "#000";
                  return {
                    name: s.name,
                    icon: s.type === "line" ? "roundRect" : "rect",
                    itemStyle: { color: color },
                  };
                });

              const exportContainer = document.createElement("div");
              exportContainer.style.position = "absolute";
              exportContainer.style.left = "-9999px";
              exportContainer.style.top = "0";
              exportContainer.style.width = "1400px";
              exportContainer.style.height = "900px";
              document.body.appendChild(exportContainer);

              const exportChart = echarts.init(exportContainer, null, { renderer: "svg" });

              const currentGrid = Array.isArray(currentOption.grid)
                ? currentOption.grid[0]
                : currentOption.grid;
              const currentXAxis = Array.isArray(currentOption.xAxis)
                ? currentOption.xAxis[0]
                : currentOption.xAxis;
              const currentYAxis = Array.isArray(currentOption.yAxis) ? currentOption.yAxis : [];

              const itemsPerRow = 4;
              const legendRows = Math.ceil(exportLegendData.length / itemsPerRow);
              const legendSpacing = legendRows * 25 + 40; // 25px per row + padding

              const variableLegendGraphics = [];
              let yOffset = 15;
              if (dragItemLeft) {
                const label = getAxisLabel(
                  dragItemLeft,
                  t,
                  moistureContentDisplayMode,
                  dragItemLeftId
                );
                variableLegendGraphics.push({
                  type: "group",
                  right: 15,
                  top: yOffset,
                  children: [
                    {
                      type: "line",
                      shape: { x1: 0, y1: 5, x2: 20, y2: 5 },
                      style: { stroke: "#102532", lineWidth: 2 },
                    },
                    {
                      type: "text",
                      left: 25,
                      style: {
                        text: label,
                        fill: "#102532",
                        font: "12px sans-serif",
                      },
                    },
                  ],
                });
                yOffset += 20;
              }
              if (dragItemRight) {
                const label = getAxisLabel(
                  dragItemRight,
                  t,
                  moistureContentDisplayMode,
                  dragItemRightId
                );
                variableLegendGraphics.push({
                  type: "group",
                  right: 15,
                  top: yOffset,
                  children: [
                    {
                      type: "line",
                      shape: { x1: 0, y1: 5, x2: 20, y2: 5 },
                      style: { stroke: "#102532", lineWidth: 2, lineDash: [4, 2] },
                    },
                    {
                      type: "text",
                      left: 25,
                      style: {
                        text: label,
                        fill: "#102532",
                        font: "12px sans-serif",
                      },
                    },
                  ],
                });
              }

              const exportOption = {
                animation: false,
                xAxis: currentXAxis,
                yAxis: currentYAxis,
                series: currentSeries,
                tooltip: currentOption.tooltip,
                dataZoom: currentOption.dataZoom,
                graphic: variableLegendGraphics,
                legend: {
                  show: true,
                  type: "plain",
                  orient: "horizontal",
                  bottom: 10,
                  left: "center",
                  padding: [5, 10],
                  itemWidth: 14,
                  itemHeight: 10,
                  itemGap: 15,
                  textStyle: {
                    fontSize: 12,
                    color: "#102532",
                  },
                  data: exportLegendData,
                },
                grid: {
                  ...currentGrid,
                  bottom: legendSpacing + 60,
                  left: 60,
                  right: 60,
                  top: 50,
                },
              };

              exportChart.setOption(exportOption);

              setTimeout(() => {
                try {
                  const svgStr = exportChart.renderToSVGString();
                  const blob = new Blob([svgStr], { type: "image/svg+xml" });
                  const url = URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `data-science-${new Date().toISOString().split("T")[0]}.svg`;
                  link.click();

                  URL.revokeObjectURL(url);
                } catch (e) {
                  console.error("Export failed:", e);
                }

                exportChart.dispose();
                document.body.removeChild(exportContainer);
              }, 500);
            },
          },
          mySaveAsCSV: {
            show: true,
            title: "CSV Export",
            icon: "path://M14,2H6A2,2,0,0,0,4,4V20a2,2,0,0,0,2,2H18a2,2,0,0,0,2-2V8L14,2M18,20H6V4h7V9h5V20M12,19L8,15h2.5V12h3v3H16L12,19",
            onclick: function () {
              const exportData = prepareChartDataForExport(
                lYaxis,
                rYaxis,
                startDate,
                endDate,
                dragItemLeft,
                dragItemRight
              );
              if (exportData.sheets.length === 0) {
                console.warn("No data to export");
                return;
              }

              // For CSV: Combine all sheets with sensor/tree/variable info
              const allRows: string[] = [];
              const headerSet = new Set<string>(["time", "tree", "sensor", "variable"]);

              // Collect all possible columns
              exportData.sheets.forEach((sheet) => {
                if (sheet.rows.length > 0) {
                  Object.keys(sheet.rows[0]).forEach((key) => {
                    if (key !== "time") headerSet.add(key);
                  });
                }
              });

              const headers = Array.from(headerSet);
              allRows.push(headers.join(";"));

              exportData.sheets.forEach((sheet) => {
                sheet.rows.forEach((row) => {
                  const values = headers.map((header) => {
                    if (header === "tree") return sheet.treeName;
                    if (header === "sensor") return sheet.sensorName;
                    if (header === "variable") return sheet.variable;
                    const value = row[header];
                    if (value === null || value === undefined) return "";
                    if (typeof value === "string" && value.includes(";")) {
                      return `"${value}"`;
                    }
                    return String(value);
                  });
                  allRows.push(values.join(";"));
                });
              });

              const csvContent = allRows.join("\n");
              const blob = new Blob(["\uFEFF" + csvContent], {
                type: "text/csv;charset=utf-8",
              });
              const dateStr = new Date().toISOString().split("T")[0];
              saveAs(blob, `data-science-export-${dateStr}.csv`);
            },
          },
          mySaveAsExcel: {
            show: true,
            title: "Excel Export",
            icon: "path://M14,2H6A2,2,0,0,0,4,4V20a2,2,0,0,0,2,2H18a2,2,0,0,0,2-2V8L14,2M18,20H6V4h7V9h5V20M12.9,14.5L15.8,19H14L12,15.6L10,19H8.2L11.1,14.5L8.2,10H10L12,13.4L14,10H15.8L12.9,14.5",
            onclick: function () {
              const exportData = prepareChartDataForExport(
                lYaxis,
                rYaxis,
                startDate,
                endDate,
                dragItemLeft,
                dragItemRight
              );
              if (exportData.sheets.length === 0) {
                console.warn("No data to export");
                return;
              }

              try {
                const wb = XLSX.utils.book_new();

                // Ensure unique sheet names before creating sheets
                ensureUniqueSheetNames(exportData.sheets);

                // Create one sheet per sensor
                exportData.sheets.forEach((sheet) => {
                  if (sheet.rows.length === 0) return;

                  const wsData = XLSX.utils.json_to_sheet(sheet.rows);
                  const colWidths = Object.keys(sheet.rows[0] || {}).map((key) => ({
                    wch: Math.max(key.length, 20),
                  }));
                  wsData["!cols"] = colWidths;
                  XLSX.utils.book_append_sheet(wb, wsData, sheet.sheetName);
                });

                // Add metadata sheet
                const metaData = [
                  ["Export Metadata"],
                  [""],
                  ["Start Date", exportData.meta.startDate],
                  ["End Date", exportData.meta.endDate],
                  ["Exported At", exportData.meta.exportedAt],
                  ["Total Sensors", exportData.meta.totalSheets],
                  ["Variable (Left)", exportData.meta.variables.left || "-"],
                  ["Variable (Right)", exportData.meta.variables.right || "-"],
                  [""],
                  ["Sensors:"],
                  ...exportData.sheets.map((s) => [s.treeName, s.sensorName, s.variable, s.axis]),
                ];
                const wsMeta = XLSX.utils.aoa_to_sheet(metaData);
                XLSX.utils.book_append_sheet(wb, wsMeta, "Metadata");

                const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
                const blob = new Blob([wbout], {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const dateStr = new Date().toISOString().split("T")[0];
                saveAs(blob, `data-science-export-${dateStr}.xlsx`);
              } catch (e) {
                console.error("Excel export failed:", e);
              }
            },
          },
          mySaveAsJSON: {
            show: true,
            title: "JSON Export",
            icon: "path://M5,3H7V5H5V10A2,2,0,0,1,3,12A2,2,0,0,1,5,14V19H7V21H5C3.93,20.73,3,20.1,3,19V15A2,2,0,0,0,1,13H0V11H1A2,2,0,0,0,3,9V5A2,2,0,0,1,5,3M19,3A2,2,0,0,1,21,5V9A2,2,0,0,0,23,11H24V13H23A2,2,0,0,0,21,15V19C21,20.1,20.07,20.73,19,21H17V19H19V14A2,2,0,0,1,21,12A2,2,0,0,1,19,10V5H17V3H19M12,15A1,1,0,1,1,11,14A1,1,0,0,1,12,15M8,15A1,1,0,1,1,7,14A1,1,0,0,1,8,15M16,15A1,1,0,1,1,15,14A1,1,0,0,1,16,15Z",
            onclick: function () {
              const exportData = prepareChartDataForExport(
                lYaxis,
                rYaxis,
                startDate,
                endDate,
                dragItemLeft,
                dragItemRight
              );
              if (exportData.sheets.length === 0) {
                console.warn("No data to export");
                return;
              }

              const exportObject = {
                meta: exportData.meta,
                sensors: exportData.sheets.map((sheet) => ({
                  treeName: sheet.treeName,
                  sensorName: sheet.sensorName,
                  variable: sheet.variable,
                  axis: sheet.axis,
                  data: sheet.rows,
                })),
              };

              const jsonContent = JSON.stringify(exportObject, null, 2);
              const blob = new Blob([jsonContent], {
                type: "application/json;charset=utf-8",
              });
              const dateStr = new Date().toISOString().split("T")[0];
              saveAs(blob, `data-science-export-${dateStr}.json`);
            },
          },
        },
      },
      grid: {
        bottom: isCompactLandscape ? 40 : 72,
        left: isCompactLandscape ? 50 : 70,
        right: gridRight,
        top: isCompactLandscape ? 6 : 10,
        containLabel: false,
      },
      xAxis: xAxis,
      yAxis: [
        {
          show: showLeftVarAxis,
          type: "value",
          name: getAxisLabel(dragItemLeft, t, moistureContentDisplayMode, dragItemLeftId),
          inverse: dragItemLeft === "tree_water_deficit",
          axisTick: {
            length: 5,
          },
          min: leftAxisBounds.min,
          max: leftAxisBounds.max,
          nameRotate: 90,
          nameGap: isCompactLandscape ? 30 : 45,
          offset: 8,
          nameLocation: "center",
          nameTextStyle: {
            verticalAlign: "middle",
            padding: isCompactLandscape ? [0, 0, 10, 20] : [0, 0, 20, 30],
          },
        },
        {
          show: showRightVarAxis,
          type: "value",
          name: getAxisLabel(dragItemRight, t, moistureContentDisplayMode, dragItemRightId),
          inverse: dragItemRight === "tree_water_deficit",
          axisTick: {
            length: 5,
          },
          min: rightAxisBounds.min,
          max: rightAxisBounds.max,
          nameRotate: 90,
          nameGap: 30,
          nameLocation: "center",
          nameTextStyle: {
            verticalAlign: "middle",
            padding: [0, 0, 0, 8],
          },
        },
        {
          boundaryGap: ["0%", "5%"],
          show: false,
          type: "value",
          name: t("health_states_title"),
          min: 0,
          max: 1,
        },
        {
          show: showRainAxis,
          name: t("rainfall_mm"),
          alignTicks: false,
          type: "value",
          min: 0,
          max: rainAxisMax,
          offset: showRightVarAxis ? 38 : 0,
          nameRotate: 90,
          nameGap: 30,
          nameLocation: "center",
          nameTextStyle: {
            verticalAlign: "middle",
            padding: [0, 0, 0, 8],
          },
        },
        {
          show: showIrrigationAxis,
          name: t("care_chat.water_amount_liters"),
          alignTicks: true,
          type: "value",
          min: 0,
          max: irrigationAxisMax,
          position: "right",
          offset: showRainAxis ? (showRightVarAxis ? 76 : 38) : showRightVarAxis ? 38 : 0,
          nameRotate: 90,
          nameGap: 30,
          nameLocation: "center",
          nameTextStyle: {
            verticalAlign: "middle",
            color: "var(--bs-primary, #0d6efd)",
            padding: [0, 0, 0, 8],
          },
          splitLine: { show: false },
        },
      ],

      tooltip:
        chartType === "histogram"
          ? { trigger: "axis" }
          : {
              trigger: "item",
              triggerOn: "none",
              axisPointer: { show: false },
              confine: true,
              backgroundColor: "var(--color-bg-surface, #ffffff)",
              borderColor: "var(--color-border-subtle, rgba(15, 23, 42, 0.12))",
              borderWidth: 1,
              padding: 0,
              textStyle: {
                color: "var(--color-text-primary, #102532)",
                fontSize: 12,
              },
              extraCssText:
                "border-radius: 10px; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18); overflow: hidden;",
              formatter: function (params) {
                const item = Array.isArray(params) ? params[0] : params;
                if (!item?.seriesName) return "";

                const markerColor =
                  item?.data?.color ||
                  item?.data?.itemStyle?.color ||
                  item?.borderColor ||
                  item?.color ||
                  item?.borderColor ||
                  "var(--bs-primary, #0d6efd)";
                const label = getSeriesLabel(item);
                const meta = getSeriesMeta(item);
                const value = formatSeriesValue(item);
                const dateLabel =
                  item?.axisValueLabel || moment.utc(item?.value?.[0]).format("DD.MM.YYYY HH:mm");
                const { treeName, sensorName } = getTooltipSource(item);

                return (
                  `<div style="min-width:260px; max-width:320px;">` +
                  `<div style="padding:10px 12px; background:var(--color-bg-subtle, rgba(16, 37, 50, 0.04)); border-bottom:1px solid var(--color-border-subtle, rgba(15, 23, 42, 0.08));">` +
                  `<div style="color:var(--color-text-secondary, #5b7181); font-size:11px; line-height:1.2;">${escapeHtml(
                    t("date")
                  )}</div>` +
                  `<div style="margin-top:2px; color:var(--color-text-primary, #102532); font-size:13px; font-weight:700; line-height:1.3;">${escapeHtml(
                    dateLabel
                  )}</div>` +
                  `</div>` +
                  `<div style="display:flex; align-items:flex-start; justify-content:space-between; gap:14px; padding:10px 12px;">` +
                  `<div style="display:flex; align-items:flex-start; gap:8px; min-width:0; flex:1;">` +
                  `<span style="width:8px; height:8px; margin-top:5px; border-radius:999px; background:${escapeHtml(
                    markerColor
                  )}; flex:0 0 auto;"></span>` +
                  `<div style="min-width:0; flex:1;">` +
                  (treeName
                    ? `<div style="color:var(--color-text-primary, #102532); font-weight:700; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(
                        treeName
                      )}</div>`
                    : "") +
                  `<div style="margin-top:2px; color:var(--color-text-primary, #102532); font-weight:600; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(
                    sensorName || label
                  )}</div>` +
                  (meta
                    ? `<div style="margin-top:2px; color:var(--color-text-secondary, #5b7181); font-size:11px; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(
                        meta
                      )}</div>`
                    : "") +
                  (item?.data?.formulaType
                    ? `<div style="margin-top:2px; color:var(--color-text-secondary, #5b7181); font-size:11px; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(
                        t("formula_type")
                      )}: ${escapeHtml(item.data.formulaType)}</div>`
                    : "") +
                  (item?.data?.calibrationSpecies
                    ? `<div style="margin-top:2px; color:var(--color-text-secondary, #5b7181); font-size:11px; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${escapeHtml(
                        t("calibration_species")
                      )}: ${escapeHtml(item.data.calibrationSpecies)}</div>`
                    : "") +
                  `</div>` +
                  `</div>` +
                  `<div style="color:var(--color-text-primary, #102532); font-weight:700; font-variant-numeric:tabular-nums; white-space:nowrap; text-align:right; flex:0 0 auto; max-width:110px;">${escapeHtml(
                    value
                  )}</div>` +
                  `</div>` +
                  `</div>`
                );
              },
            },
      dataZoom:
        chartType === "histogram"
          ? []
          : [
              {
                type: "inside",
                xAxisIndex: 0,
                start: 0,
                end: 100,
              },
            ],
      series: chartType === "histogram" ? histogramSeries : computedSeries,
    };
  };

  const chartRef = useRef(null);
  const ignoreDataZoomUntilRef = useRef(0);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const seriesRef = useRef([]);
  const lastHoverRef = useRef(null);

  useEffect(() => {
    return () => {
      const chart = chartInstance.current;
      const isDisposed = typeof chart?.isDisposed === "function" ? chart.isDisposed() : false;
      const zr = !isDisposed ? chart?.getZr?.() : null;

      if (!isDisposed && typeof chart?.off === "function") {
        chart.off("datazoom");
      }
      if (typeof zr?.off === "function") {
        zr.off("mousemove");
        zr.off("globalout");
      }

      chartInstance.current = null;
      chartRef.current = null;
      lastHoverRef.current = null;
    };
  }, []);

  // Trigger ECharts resize on orientation/window changes
  useEffect(() => {
    const handleResize = () => {
      const instance = chartRef.current?.getEchartsInstance?.();
      if (instance && !instance.isDisposed?.()) {
        instance.resize();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    startDateRef.current = startDate;
    endDateRef.current = endDate;
  }, [startDate, endDate]);

  const buildSeriesSignature = (seriesList) =>
    (seriesList || [])
      .map((s) => {
        const data = s?.data || [];
        const first = data[0]?.value?.[0] ?? "";
        const last = data[data.length - 1]?.value?.[0] ?? "";
        const opacity = s?.lineStyle?.opacity ?? "";
        const markLineSignature = (s?.markLine?.data || [])
          .map((ml) => `${ml?.yAxis ?? ""}:${ml?.label?.formatter ?? ""}`)
          .join(",");
        return `${s?.name || ""}|${s?.type || ""}|${s?.yAxisIndex ?? ""}|${
          data.length
        }|${first}|${last}|${opacity}|${markLineSignature}`;
      })
      .join(";");

  const computedSeries = useMemo(() => {
    const leftDataMean = lYaxisMean?.map((data) => ({
      name: data.name,
      yAxisIndex: 0,
      smooth: true,
      type: "line",
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: data ? data.color : "transparent",
        type: "solid",
      },
      data: data.data,
    }));
    const rightDataMean = rYaxisMean?.map((data) => ({
      name: data.name,
      yAxisIndex: 1,
      smooth: true,
      type: "line",
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: data ? data.color : "transparent",
        type: "dotted",
      },
      data: data.data,
    }));

    const leftHasPerPoint = showMoistureThresholds && hasPerPointThresholds(lYaxis);
    const rightHasPerPoint = showMoistureThresholds && hasPerPointThresholds(rYaxis);
    const leftPerPointSeries = leftHasPerPoint
      ? buildPerPointThresholdSeries(lYaxis, 0, "left")
      : [];
    const rightPerPointSeries = rightHasPerPoint
      ? buildPerPointThresholdSeries(rYaxis, 1, "right")
      : [];

    const leftMarkLines =
      moistureThresholdsVisible && !leftHasPerPoint && thresholdTree
        ? getMarkLines(thresholdTree, dragItemLeft)
        : [];
    const rightMarkLines =
      moistureThresholdsVisible && !rightHasPerPoint && thresholdTree
        ? getMarkLines(thresholdTree, dragItemRight)
        : [];
    const leftHasMoistureThresholds = leftMarkLines.length > 0;
    const rightHasMoistureThresholds = rightMarkLines.length > 0;
    const leftThresholdSeries = leftHasMoistureThresholds
      ? buildThresholdSeries(leftMarkLines, 0)
      : [];
    const rightThresholdSeries = rightHasMoistureThresholds
      ? buildThresholdSeries(rightMarkLines, 1)
      : [];

    const leftThresholdHostIndex = (lYaxis || []).findIndex(
      (entry) => Array.isArray(entry?.data) && entry.data.length > 0
    );
    const rightThresholdHostIndex = (rYaxis || []).findIndex(
      (entry) => Array.isArray(entry?.data) && entry.data.length > 0
    );

    const leftData = lYaxis?.map((data, index) => ({
      name: data.name,
      smooth: true,
      type: "line",
      yAxisIndex: 0,
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: data.color,
        opacity: showHideLeftData ? 0 : 1,
        type: "solid",
      },
      data: showHideLeftData ? [] : data.data,
      ...(leftHasMoistureThresholds &&
      (leftThresholdHostIndex === -1 ? index === 0 : index === leftThresholdHostIndex)
        ? {
            markLine: {
              symbol: "none",
              label: { show: false },
              lineStyle: { type: "dashed", width: 1 },
              data: leftMarkLines,
            },
          }
        : {}),
    }));
    const rightData = rYaxis?.map((data, index) => ({
      name: data.name,
      yAxisIndex: 1,
      smooth: true,
      type: "line",
      showSymbol: false,
      lineStyle: {
        width: 2,
        color: data.color,
        opacity: showHideRightData ? 0 : 1,
        type: "dotted",
      },
      data: showHideRightData ? [] : data.data,
      ...(rightHasMoistureThresholds &&
      (rightThresholdHostIndex === -1 ? index === 0 : index === rightThresholdHostIndex)
        ? {
            markLine: {
              symbol: "none",
              label: { show: false },
              lineStyle: { type: "dashed", width: 1 },
              data: rightMarkLines,
            },
          }
        : {}),
    }));
    const seriesNewRaw =
      chartType === "histogram"
        ? histogramSeries
        : [
            ...leftData,
            ...leftThresholdSeries,
            ...leftPerPointSeries,
            leftDataMean,
            rightData,
            ...rightThresholdSeries,
            ...rightPerPointSeries,
            rightDataMean,
            healthData,
            irrigationData,
            rainData,
          ].flat();
    const seriesNew = seriesNewRaw.map(decorateSeriesForHover);
    return seriesNew;
  }, [
    lYaxis,
    rYaxis,
    lYaxisMean,
    rYaxisMean,
    healthData,
    rainData,
    irrigationData,
    showHideLeftData,
    showHideRightData,
    chartType,
    dragItemLeft,
    dragItemRight,
    moistureThresholdsVisible,
    showMoistureThresholds,
    thresholdTree,
    startDate,
    endDate,
  ]);

  const computedSeriesSignature = useMemo(
    () => buildSeriesSignature(computedSeries),
    [computedSeries]
  );

  useEffect(() => {
    seriesRef.current = computedSeries;
  }, [computedSeries]);

  useEffect(() => {
    if (!computedSeriesSignature) return;
    ignoreDataZoomUntilRef.current = Date.now() + 900;
  }, [computedSeriesSignature]);

  const onChartReady = (chart) => {
    chartInstance.current = chart;
    const zr = chart?.getZr?.();
    const parseZoomRange = (eventParams) => {
      const payload =
        Array.isArray(eventParams?.batch) && eventParams.batch.length
          ? eventParams.batch[0]
          : eventParams;

      let startValue = payload?.startValue;
      let endValue = payload?.endValue;

      if (startValue == null || endValue == null) {
        const options = chart.getOption?.();
        const dz = Array.isArray(options?.dataZoom) ? options.dataZoom[0] : null;
        startValue = startValue ?? dz?.startValue;
        endValue = endValue ?? dz?.endValue;
      }

      if (startValue == null || endValue == null) return null;

      const start = moment.utc(startValue);
      const end = moment.utc(endValue);
      if (!start.isValid() || !end.isValid()) return null;

      return {
        startDateNext: start.format("YYYY-MM-DD"),
        endDateNext: end.format("YYYY-MM-DD"),
      };
    };

    const onDataZoom = (eventParams) => {
      if (Date.now() < ignoreDataZoomUntilRef.current) return;
      const range = parseZoomRange(eventParams);
      if (!range) return;

      const { startDateNext, endDateNext } = range;
      if (startDateRef.current === startDateNext && endDateRef.current === endDateNext) return;

      setZoomTriggeredDateChange(true);
      setStartDate(startDateNext);
      setEndDate(endDateNext);
    };

    if (typeof chart?.off === "function") {
      chart.off("datazoom");
    }
    if (typeof chart?.on === "function") {
      chart.on("datazoom", onDataZoom);
    }

    const hideNearestTooltip = () => {
      const currentHover = lastHoverRef.current;
      if (currentHover) {
        chart.dispatchAction({
          type: "downplay",
          seriesIndex: currentHover.seriesIndex,
          dataIndex: currentHover.dataIndex,
        });
      }
      chart.dispatchAction({ type: "hideTip" });
      lastHoverRef.current = null;
    };

    const showNearestTooltip = (event) => {
      if (!zr) return;

      const cursor = [event?.offsetX ?? 0, event?.offsetY ?? 0];
      if (!chart.containPixel({ gridIndex: 0 }, cursor)) {
        hideNearestTooltip();
        return;
      }

      const visibleSeries = (seriesRef.current || [])
        .map((entry, seriesIndex) => ({ entry, seriesIndex }))
        .filter(
          ({ entry }) =>
            entry &&
            !entry.silent &&
            entry.type !== "custom" &&
            Array.isArray(entry.data) &&
            entry.data.length > 0 &&
            (entry.lineStyle?.opacity ?? 1) > 0
        );

      let nearestPoint = null;

      visibleSeries.forEach(({ entry: seriesEntry, seriesIndex }) => {
        const dataIndex = findClosestPointIndex(chart, seriesIndex, seriesEntry.data, cursor);
        if (dataIndex < 0) return;

        const point = seriesEntry.data[dataIndex];
        const pixel = chart.convertToPixel({ seriesIndex }, point.value);
        if (!Array.isArray(pixel) || pixel.length < 2) return;

        const dx = pixel[0] - cursor[0];
        const dy = pixel[1] - cursor[1];
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (!nearestPoint || distance < nearestPoint.distance) {
          nearestPoint = { seriesIndex, dataIndex, distance };
        }
      });

      if (!nearestPoint) {
        hideNearestTooltip();
        return;
      }

      const lastHover = lastHoverRef.current;
      if (
        lastHover &&
        lastHover.seriesIndex === nearestPoint.seriesIndex &&
        lastHover.dataIndex === nearestPoint.dataIndex
      ) {
        return;
      }

      if (lastHover) {
        chart.dispatchAction({
          type: "downplay",
          seriesIndex: lastHover.seriesIndex,
          dataIndex: lastHover.dataIndex,
        });
      }

      chart.dispatchAction({
        type: "highlight",
        seriesIndex: nearestPoint.seriesIndex,
        dataIndex: nearestPoint.dataIndex,
      });
      chart.dispatchAction({
        type: "showTip",
        seriesIndex: nearestPoint.seriesIndex,
        dataIndex: nearestPoint.dataIndex,
      });

      lastHoverRef.current = nearestPoint;
    };

    if (typeof zr?.off === "function") {
      zr.off("mousemove");
      zr.off("globalout");
    }
    if (typeof zr?.on === "function") {
      zr.on("mousemove", showNearestTooltip);
      zr.on("globalout", hideNearestTooltip);
    }
  };

  return (
    <ReactEcharts option={getOption()} style={style} onChartReady={onChartReady} ref={chartRef} />
  );
}

export default GraphArea;
